<!-- SwarmDashboard.vue -->
<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="mb-6">
          <v-card-title class="py-2">
            <span class="text-h5">Swarm Dashboard</span>
            <v-spacer></v-spacer>
            <v-btn
                color="primary"
                @click="refreshData"
                :loading="loading"
                :disabled="loading"
            >
              <template v-if="loading">
                <v-progress-circular
                    indeterminate
                    size="20"
                    width="2"
                    class="mr-2"
                />
              </template>
              <v-icon v-else left>mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="isGuest" class="mt-n4 mb-2">
      <v-col cols="12" class="py-0">
        <v-alert type="info" text dense border="left" class="mb-0">
          <v-icon left small>mdi-eye-outline</v-icon>
          Read-only access — you can browse services but can't deploy or scale. Ask an admin to grant deploy permission.
        </v-alert>
      </v-col>
    </v-row>

    <v-row class="mb-6">
      <v-col cols="12" lg="8">
        <v-card height="100%">
          <v-card-title class="subtitle-1">
            <v-icon left color="primary">mdi-view-dashboard</v-icon>
            System Overview
          </v-card-title>
          <v-divider></v-divider>

          <v-card-text>
            <v-fade-transition>
              <div v-if="initialLoading" class="d-flex justify-center align-center my-12">
                <v-progress-circular indeterminate size="64"></v-progress-circular>
              </div>
              <div v-else>
                <v-row>
                  <v-col cols="12" sm="6" md="3">
                    <v-card outlined class="metric-card">
                      <v-card-text class="text-center">
                        <div class="text-overline grey--text mb-2">Services</div>
                        <div class="text-h4 font-weight-bold text-primary">{{ totalServices }}</div>
                        <div class="subtitle-2" :class="runningServicesColor">{{ runningServices }} Running</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-card outlined class="metric-card">
                      <v-card-text class="text-center">
                        <div class="text-overline grey--text mb-2">Stacks</div>
                        <div class="text-h4 font-weight-bold text-primary">{{ totalStacks }}</div>
                        <div class="subtitle-2 text--secondary">{{ totalStacks }} Active</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-card outlined class="metric-card">
                      <v-card-text class="text-center">
                        <div class="text-overline grey--text mb-2">Replicas</div>
                        <div class="text-h4 font-weight-bold text-primary">{{ totalReplicas }}</div>
                        <div class="subtitle-2 text--secondary">Total Instances</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-card outlined class="metric-card">
                      <v-card-text class="text-center">
                        <div class="text-overline grey--text mb-2">Node Status</div>
                        <div class="text-h4 font-weight-bold" :class="getNodeStatusColor(mainNode?.Status?.State)">
                          {{ mainNode?.Status?.State || 'N/A' }}
                        </div>
                        <div class="subtitle-2 text--secondary">System State</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <v-row class="mt-4">
                  <v-col cols="12" md="6">
                    <v-card outlined height="100%">
                      <v-card-text>
                        <div class="text-subtitle-1 mb-3">System Resources</div>

                        <div class="mb-6">
                          <div class="d-flex justify-space-between align-center mb-1">
                            <span class="text-body-2">Total CPU</span>
                            <span class="text-body-2 font-weight-medium">
                              {{ formatCPU(mainNode?.Description?.Resources?.NanoCPUs) }} Cores
                            </span>
                          </div>
                          <v-progress-linear
                              :value="calculateCPUUsage(mainNode)"
                              :color="getResourceColor(calculateCPUUsage(mainNode))"
                              height="8"
                              rounded
                          >
                            <template v-slot:default="{ value }">
                              <small>{{ Math.ceil(value) }}%</small>
                            </template>
                          </v-progress-linear>
                        </div>

                        <div>
                          <div class="d-flex justify-space-between align-center mb-1">
                            <span class="text-body-2">Total Memory</span>
                            <span class="text-body-2 font-weight-medium">
                              {{ formatBytes(mainNode?.Description?.Resources?.MemoryBytes) }}
                            </span>
                          </div>
                          <v-progress-linear
                              :value="calculateMemoryUsage(mainNode)"
                              :color="getResourceColor(calculateMemoryUsage(mainNode))"
                              height="8"
                              rounded
                          >
                            <template v-slot:default="{ value }">
                              <small>{{ Math.ceil(value) }}%</small>
                            </template>
                          </v-progress-linear>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-card outlined height="100%">
                      <v-card-text>
                        <div class="text-subtitle-1 mb-3">Service Health</div>

                        <div class="d-flex justify-space-between align-center mt-7 mb-3">
                          <div>
                            <div class="text-h5 font-weight-bold">{{ runningServices }}</div>
                            <div class="text-caption">Running</div>
                          </div>
                          <div>
                            <div class="text-h5 font-weight-bold">{{ globalServices }}</div>
                            <div class="text-caption">Global</div>
                          </div>
                          <div>
                            <div class="text-h5 font-weight-bold text--error">{{ stoppedServices }}</div>
                            <div class="text-caption">Stopped</div>
                          </div>
                        </div>

                        <v-divider class="mb-3"></v-divider>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </v-fade-transition>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="4">
        <v-card height="100%">
          <v-card-title class="subtitle-1">
            <v-icon left color="primary">mdi-server</v-icon>
            Node Status
          </v-card-title>
          <v-divider></v-divider>

          <v-card-text>
            <v-fade-transition>
              <div v-if="initialLoading" class="d-flex justify-center align-center my-12">
                <v-progress-circular indeterminate size="64"></v-progress-circular>
              </div>
              <div v-else>
                <div v-if="mainNode">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="text-h6">
                        {{ mainNode.Description?.Hostname || 'Unknown Host' }}
                        <v-chip
                            small
                            :color="getNodeStatusColor(mainNode.Status?.State)"
                            dark
                            class="ml-2"
                        >
                          {{ mainNode.Status?.State || 'unknown' }}
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ mainNode.Status?.Addr || 'No address available' }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-card flat class="mt-0">
                    <v-card-text>
                      <div class="d-flex align-center mb-0">
                        <span class="grey--text">CPU Usage</span>
                        <span class="ml-auto">{{ calculateCPUUsage(mainNode) }}%</span>
                      </div>
                      <v-progress-linear
                          :value="calculateCPUUsage(mainNode)"
                          :color="getResourceColor(calculateCPUUsage(mainNode))"
                          height="8"
                          rounded
                      ></v-progress-linear>

                      <div class="d-flex align-center mb-0 mt-3">
                        <span class="grey--text">Memory Usage</span>
                        <span class="ml-auto">{{ calculateMemoryUsage(mainNode) }}%</span>
                      </div>
                      <v-progress-linear
                          :value="calculateMemoryUsage(mainNode)"
                          :color="getResourceColor(calculateMemoryUsage(mainNode))"
                          height="8"
                          rounded
                      ></v-progress-linear>
                    </v-card-text>
                  </v-card>

                  <v-list dense class="mt-0">
                    <v-subheader>System Information</v-subheader>
                    <v-list-item>
                      <v-list-item-icon class="mr-2">
                        <v-icon small>mdi-desktop-classic</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="caption">
                          OS: {{ mainNode.Description?.Platform?.OS || 'Unknown' }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-icon class="mr-2">
                        <v-icon small>mdi-chip</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="caption">
                          Architecture: {{ mainNode.Description?.Platform?.Architecture || 'Unknown' }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-icon class="mr-2">
                        <v-icon small>mdi-docker</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="caption">
                          Docker: {{ mainNode.Description?.Engine?.EngineVersion || 'Unknown' }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>
                <v-alert
                    v-else
                    type="warning"
                    text
                    dense
                >
                  No node information available
                </v-alert>
              </div>
            </v-fade-transition>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <div v-for="(services, stackName) in groupedServices" :key="stackName" class="mb-6">
          <v-card>
            <v-card-title
                class="stack-header py-2"
                style="cursor: pointer"
                @click="toggleStack(stackName)"
            >
              <v-icon left :color="getStackColor(stackName)">{{ getStackIcon(stackName) }}</v-icon>
              Stack: {{ stackName }}
              <v-chip class="ml-4" small>{{ services.length }} services</v-chip>
              <v-spacer></v-spacer>
              <v-btn icon>
                <v-icon>{{ expandedStacks[stackName] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </v-btn>
            </v-card-title>

            <v-expand-transition>
              <div v-show="expandedStacks[stackName]">
                <v-divider></v-divider>
                <v-card-text>
                  <v-row>
                    <v-col
                        v-for="service in services"
                        :key="service.ID"
                        cols="12"
                        md="6"
                        lg="4"
                    >
                      <v-hover v-slot="{ hover }">
                        <v-card
                            :elevation="hover ? 8 : 2"
                            :class="{ 'on-hover': hover }"
                            class="service-card"
                            height="100%"
                        >
                          <v-card-title class="subtitle-2" :class="getServiceHeaderClass(service)">
                            {{ getServiceName(service) }}
                            <v-spacer></v-spacer>
                            <v-chip
                                x-small
                                :color="getServiceStatusColor(service)"
                            >
                              {{ getServiceStatus(service) }}
                            </v-chip>
                            <v-chip
                                x-small
                                :color="getReplicaStatusColor(service)"
                                class="ml-2"
                                v-if="!service.Spec.Mode.Global"
                            >
                              {{ getReplicaCount(service) }}
                            </v-chip>
                            <v-btn
                                icon
                                x-small
                                class="ml-2"
                                @click.stop="refreshService(service)"
                                :disabled="loading || service._loading"
                            >
                              <v-progress-circular
                                  v-if="service._loading"
                                  indeterminate
                                  size="16"
                              ></v-progress-circular>
                              <v-icon v-else small>mdi-refresh</v-icon>
                            </v-btn>
                          </v-card-title>

                          <v-divider></v-divider>

                          <v-card-text>
                            <v-list dense>
                              <v-list-item>
                                <v-list-item-icon class="mr-2">
                                  <v-icon small>mdi-docker</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title
                                      class="caption"
                                      :class="{'cursor-pointer': isPrivateImage(service)}"
                                      @click.stop="isPrivateImage(service) && showImageTags(service)"
                                  >
                                    {{ getImageName(service) }}
                                    <v-icon x-small class="ml-1" v-if="isPrivateImage(service)">mdi-tag-multiple</v-icon>
                                  </v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>

                              <v-list-item>
                                <v-list-item-icon class="mr-2">
                                  <v-icon small>mdi-server</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title class="caption">
                                    Mode: {{ getServiceMode(service) }}
                                    <template v-if="service.Spec.Mode.Replicated">
                                      ({{ service.Spec.Mode.Replicated.Replicas }} replicas)
                                    </template>
                                  </v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>

                              <v-list-item>
                                <v-list-item-icon class="mr-2">
                                  <v-icon small>mdi-lan-connect</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title class="caption">
                                    Ports: {{ getPortMapping(service) }}
                                  </v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>

                              <v-list-item>
                                <v-list-item-icon class="mr-2">
                                  <v-icon small>mdi-memory</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title class="caption">
                                    Memory: {{ getMemoryLimits(service) }}
                                  </v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>
                              <v-list-item v-if="service.Spec.Mode.Global">
                                <v-list-item-icon class="mr-2">
                                  <v-icon small>mdi-server-network</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title class="caption">
                                    Global Mode (1 per node)
                                  </v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>
                              <v-list-item v-else>
                                <v-list-item-icon class="mr-2">
                                  <v-icon small>mdi-server-network</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <div class="d-flex align-center">
                                    <div class="caption mr-4">
                                      <span class="font-weight-medium">Scale: </span>
                                      <span class="scale-control ml-2">
                                        <v-btn
                                            v-if="isAdmin"
                                            text
                                            x-small
                                            class="scale-btn"
                                            :disabled="service.Spec.Mode.Replicated.Replicas <= 0"
                                            @click="scaleService(service, service.Spec.Mode.Replicated.Replicas - 1)"
                                        >
                                            <v-icon x-small>mdi-minus</v-icon>
                                        </v-btn>
                                        <span class="scale-value">{{ service.Spec.Mode.Replicated.Replicas }}</span>
                                        <v-btn
                                            v-if="isAdmin"
                                            text
                                            x-small
                                            class="scale-btn"
                                            @click="scaleService(service, service.Spec.Mode.Replicated.Replicas + 1)"
                                        >
                                            <v-icon x-small>mdi-plus</v-icon>
                                        </v-btn>
                                      </span>
                                    </div>
                                  </div>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-card-text>

                          <v-divider></v-divider>

                          <v-card-actions>
                            <v-btn
                                small
                                text
                                @click="showImageTags(service)"
                                v-if="canDeployService(service, stackName)"
                            >
                              <v-icon left small>mdi-rocket-launch</v-icon>
                              Deploy
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn
                                small
                                text
                                @click="showServiceDetails(service)"
                            >
                              <v-icon left small>mdi-information</v-icon>
                              Details
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-hover>
                    </v-col>
                  </v-row>
                </v-card-text>
              </div>
            </v-expand-transition>
          </v-card>
        </div>
      </v-col>
    </v-row>

    <image-tags-dialog
        v-model="imageTagsDialog"
        :key="selectedService.ID"
        :service="selectedService"
        :clst-nm="profileReq.clstNm"
        v-if="selectedService"
        @deployed="refreshData"
    />

    <v-dialog
        v-model="detailsDialog"
        max-width="1200"
    >
      <service-details
          v-if="selectedService"
          :service="selectedService"
          :loading="selectedService._loading"
          @close="detailsDialog = false"
          @refresh="refreshService(selectedService)"
      />
    </v-dialog>

    <v-snackbar
        v-model="showError"
        color="error"
        timeout="5000"
    >
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            v-bind="attrs"
            @click="showError = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import swarmApi from '@/config/api/swarm'
import ServiceDetails from './ServiceDetails.vue'
import ImageTagsDialog from './ImageTagsDialog.vue'
import prometheus from "@/config/api/prometheus"
import { mapGetters } from 'vuex'

export default {
  name: 'Swarm',

  components: {
    ServiceDetails,
    ImageTagsDialog
  },

  data: () => ({
    initialLoading: true,
    imageTagsDialog: false,
    loading: false,
    updating: false,
    nodes: [],
    services: [],
    detailsDialog: false,
    selectedService: null,
    refreshInterval: null,
    showError: false,
    errorMessage: '',
    profileReq: { clstNm: "demo" },
    expandedStacks: { demo: true },
    metrics: null,
    metricsInterval: null,
  }),

  computed: {
    ...mapGetters(['isAdmin', 'isUser', 'isGuest', 'deployableStacks']),

    mainNode() {
      return this.nodes[0] || null;
    },

    groupedServices() {
      const groups = {}
      this.services.forEach(service => {
        const stackName = service.Spec?.Labels?.['com.docker.stack.namespace'] || 'Default Stack';
        if (!groups[stackName]) {
          groups[stackName] = [];
        }
        groups[stackName].push(service);
      });

      // Sort services within each stack
      Object.keys(groups).forEach(stack => {
        groups[stack].sort((a, b) => this.getServiceName(a).localeCompare(this.getServiceName(b)));
      });

      // Sort stacks alphabetically
      const orderedGroups = {};
      Object.keys(groups).sort().forEach(key => {
        orderedGroups[key] = groups[key];
      });

      return orderedGroups;
    },

    totalServices() {
      return this.services.length;
    },

    runningServices() {
      return this.services.filter(s => this.getServiceStatus(s) === 'RUNNING').length;
    },

    totalStacks() {
      return Object.keys(this.groupedServices).length;
    },

    totalReplicas() {
      return this.services.reduce((total, service) => {
        if (service.Spec?.Mode?.Replicated) {
          return total + (service.Spec.Mode.Replicated.Replicas || 0);
        }
        return total + (service.Spec?.Mode?.Global ? 1 : 0);
      }, 0);
    },

    globalServices() {
      return this.services.filter(s => s.Spec?.Mode?.Global).length;
    },

    stoppedServices() {
      return this.services.filter(s => this.getServiceStatus(s) === 'STOPPED').length;
    },

    healthyServices() {
      return this.services.filter(s => {
        const status = this.getServiceStatus(s);
        return status === 'RUNNING' || status === 'GLOBAL';
      }).length;
    },

    serviceStatusBreakdown() {
      const breakdown = {
        RUNNING: 0,
        GLOBAL: 0,
        STOPPED: 0,
        UNKNOWN: 0
      };

      this.services.forEach(service => {
        const status = this.getServiceStatus(service);
        breakdown[status] = (breakdown[status] || 0) + 1;
      });

      return Object.entries(breakdown)
          .filter(([_, count]) => count > 0)
          .map(([status, count]) => ({ status, count }));
    },

    runningServicesColor() {
      const percentage = (this.runningServices / this.totalServices) * 100;
      if (percentage >= 90) return 'success--text';
      if (percentage >= 70) return 'warning--text';
      return 'error--text';
    }
  },

  methods: {
    isPrivateImage(service) {
      const image = this.getImageName(service);
      return image.startsWith('docker.innilabs.co.id/');
    },

    // 배포 버튼 노출 = SCM 관리 이미지 + 롤(admin=전체 스택, user=demo). 백엔드가 최종 게이트.
    canDeployService(service, stackName) {
      if (!this.isPrivateImage(service)) return false;
      return this.isAdmin || (this.isUser && this.deployableStacks.includes(stackName));
    },

    showImageTags(service) {
      if (!this.isPrivateImage(service)) return;
      this.selectedService = service;
      this.imageTagsDialog = true;
    },

    async refreshService(service) {
      this.$set(service, '_loading', true);
      try {
        const [services, tasks] = await Promise.all([
          swarmApi.listServices(this.profileReq),
          swarmApi.listServiceTasks({
            clstNm: this.profileReq.clstNm,
            serviceId: service.ID
          })
        ]);

        const updatedService = services.find(s => s.ID === service.ID);
        if (updatedService) {
          const serviceIndex = this.services.findIndex(s => s.ID === service.ID);
          if (serviceIndex !== -1) {
            const newService = {
              ...updatedService,
              _tasks: tasks,
              _loading: false
            };
            this.$set(this.services, serviceIndex, newService);
            if (this.selectedService?.ID === service.ID) {
              this.selectedService = newService;
            }
          }
        }
      } catch (error) {
        console.error('Failed to refresh service:', error);
        this.$notify.error(
            'Refresh Failed',
            `Failed to refresh ${this.getServiceName(service)}`
        );
      } finally {
        this.$set(service, '_loading', false);
      }
    },

    async updateServiceImage(newImage) {
      try {
        const imageWithTag = this.selectedTag ?
            `${this.imageName.split(':')[0]}:${this.selectedTag.tag}` :
            newImage;

        this.$notify.success(
            'Image Update Started',
            'The system is pulling the new image. This process may take a few moments depending on image size and network conditions.'
        );

        await swarmApi.updateServiceImage({
          clstNm: this.profileReq.clstNm,
          serviceId: this.selectedService.ID,
          newImage: imageWithTag,
          updateConfig: {
            parallelism: 1,
            delay: 10,
            failureAction: "pause"
          }
        });

        this.$notify.success(
            'Image Updated',
            'Service image has been updated successfully'
        );

        await this.refreshData();
      } catch (error) {
        console.error('Failed to update service image:', error);
        this.$notify.error(
            'Update Failed',
            `Failed to update service image: ${error.message || 'Unknown error'}`
        );
      }
    },

    async refreshData() {
      try {
        this.loading = true;
        const [servicesResponse, nodesResponse, metricsResponse] = await Promise.all([
          swarmApi.listServicesWithTasks(this.profileReq),
          swarmApi.listNodes(this.profileReq),
          prometheus.getNodeMetrics(this.profileReq)
        ]);

        this.services = servicesResponse.map(data => ({
          ...data.service,
          _tasks: data.tasks
        }));

        this.nodes = nodesResponse;
        this.metrics = metricsResponse;
      } catch (error) {
        console.error('Failed to load data:', error);
        this.errorMessage = `Failed to load Swarm data: ${error.message}`;
        this.showError = true;
      } finally {
        this.loading = false;
        this.initialLoading = false;
      }
    },

    async fetchMetrics() {
      try {
        const response = await prometheus.getNodeMetrics(this.profileReq);
        this.metrics = response;
      } catch (error) {
        console.error('Failed to load metrics:', error);
      }
    },

    toggleStack(stackName) {
      this.$set(this.expandedStacks, stackName, !this.expandedStacks[stackName]);
    },

    getNodeStatusColor(state) {
      const colors = {
        'ready': 'success',
        'down': 'error',
        'disconnected': 'warning'
      };
      return colors[state?.toLowerCase()] || 'grey';
    },

    calculateCPUUsage(node) {
      if (!node?.Description?.Resources?.NanoCPUs || !this.metrics) return 0;
      return Number(this.metrics.cpuUsagePercent).toFixed(2);
    },

    calculateMemoryUsage(node) {
      if (!node?.Description?.Resources?.MemoryBytes || !this.metrics) return 0;
      return Number(this.metrics.memoryUsedPercent).toFixed(2);
    },

    getResourceColor(percentage) {
      if (percentage >= 90) return 'error';
      if (percentage >= 70) return 'warning';
      if (percentage >= 50) return 'info';
      return 'success';
    },

    getStackIcon(stackName) {
      const icons = {
        'monitoring': 'mdi-monitor-dashboard',
        'demo': 'mdi-apps',
        'efk': 'mdi-text-search'
      };
      return icons[stackName.toLowerCase()] || 'mdi-package-variant';
    },

    getStackColor(stackName) {
      const colors = {
        'monitoring': 'blue',
        'demo': 'green',
        'efk': 'purple'
      };
      return colors[stackName.toLowerCase()] || 'grey';
    },

    getServiceName(service) {
      return service.Spec.Name.split('_').pop();
    },

    getImageName(service) {
      const image = service.Spec.TaskTemplate.ContainerSpec.Image;
      return image.split('@')[0];
    },

    getServiceMode(service) {
      return service.Spec.Mode.Global ? 'Global' : 'Replicated';
    },

    getPortMapping(service) {
      if (!service.Endpoint?.Spec?.Ports) return 'No ports exposed';
      return service.Endpoint.Spec.Ports
          .map(port => `${port.PublishedPort}:${port.TargetPort}`)
          .join(', ');
    },

    getMemoryLimits(service) {
      const resources = service.Spec.TaskTemplate.Resources;
      if (!resources?.Limits?.MemoryBytes) return 'No limit';
      return this.formatBytes(resources.Limits.MemoryBytes);
    },

    getServiceStatus(service) {
      if (!service?.Spec?.Mode) return 'UNKNOWN';
      if (service.Spec.Mode.Global) return 'GLOBAL';

      const tasks = service._tasks || [];
      if (tasks.some(task => task.Status.State === 'preparing' || task.Status.State === 'starting')) {
        return 'STARTING';
      }
      if (tasks.some(task => task.Status.State === 'pending')) {
        return 'PENDING';
      }
      if (tasks.some(task => task.Status.State === 'new')) {
        return 'CREATING';
      }

      const desired = service.Spec.Mode.Replicated?.Replicas || 0;
      return desired > 0 ? 'RUNNING' : 'STOPPED';
    },

    getServiceStatusColor(service) {
      const status = this.getServiceStatus(service);
      const colors = {
        'RUNNING': 'success',
        'STOPPED': 'error',
        'DEGRADED': 'warning',
        'GLOBAL': 'info',
        'STARTING': 'info',
        'PENDING': 'warning',
        'CREATING': 'info',
        'UNKNOWN': 'grey'
      };
      return colors[status];
    },

    getReplicaStatus(service) {
      if (service.Spec.Mode.Global) return 'GLOBAL';
      const desired = service.Spec.Mode.Replicated.Replicas;
      const running = this.getRunningTasks(service);
      return `${running}/${desired}`;
    },

    getReplicaCount(service) {
      const desired = service.Spec.Mode.Replicated?.Replicas || 0;
      const running = this.getRunningTasks(service);
      return `${running}/${desired}`;
    },

    getReplicaStatusColor(service) {
      if (service.Spec.Mode.Global) return 'info';
      const desired = service.Spec.Mode.Replicated.Replicas;
      const running = this.getRunningTasks(service);
      if (running === 0) return 'error';
      if (running < desired) return 'warning';
      return 'success';
    },

    getRunningTasks(service) {
      return service._tasks?.filter(task => task.Status.State === 'running').length || 0;
    },

    hasShutdownTasks(service) {
      return service._tasks?.some(task =>
          task.Status.State === 'shutdown' ||
          task.Status.State === 'failed' ||
          task.Status.State === 'rejected'
      ) || false;
    },

    getTaskStatusColor(state) {
      const colors = {
        'running': 'success',
        'starting': 'info',
        'pending': 'warning',
        'failed': 'error',
        'shutdown': 'grey',
        'rejected': 'error',
        'preparing': 'info',
        'new': 'info'
      };
      return colors[state] || 'grey';
    },
    getServiceHeaderClass(service) {
      if (service.Spec.Mode.Global) {
        return 'global-service';
      }
      const image = service.Spec.TaskTemplate.ContainerSpec.Image;
      if (image.includes('docker.innilabs.co.id')) {
        return 'innilabs-service';
      }
      return 'other-service';
    },

    getTaskStatusIcon(state) {
      const icons = {
        'running': 'mdi-check-circle',
        'starting': 'mdi-clock-outline',
        'pending': 'mdi-clock-alert-outline',
        'failed': 'mdi-alert-circle',
        'shutdown': 'mdi-power',
        'rejected': 'mdi-close-circle',
        'preparing': 'mdi-cog',
        'new': 'mdi-plus-circle-outline'
      };
      return icons[state] || 'mdi-help-circle-outline';
    },

    getTaskName(task) {
      return task.Slot ? `replica.${task.Slot}` : `task.${task.ID.substring(0, 8)}`;
    },

    getNodeName(nodeId) {
      const node = this.nodes.find(n => n.ID === nodeId);
      return node ? node.Description.Hostname : nodeId.substring(0, 8);
    },

    formatBytes(bytes) {
      if (!bytes) return 'N/A';
      const units = ['B', 'KB', 'MB', 'GB', 'TB'];
      let value = bytes;
      let unitIndex = 0;

      while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex++;
      }

      return `${value.toFixed(1)} ${units[unitIndex]}`;
    },

    formatCPU(nanoCPUs) {
      if (!nanoCPUs) return 'N/A';
      return (nanoCPUs / 1000000000).toFixed(1);
    },

    showServiceDetails(service) {
      this.selectedService = service;
      this.detailsDialog = true;
    },

    async scaleService(service, newReplicas) {
      try {
        await swarmApi.scaleService({
          clstNm: this.profileReq.clstNm,
          serviceId: service.ID,
          replicas: newReplicas
        });

        this.$notify.success(
            'Service Scaled',
            `${this.getServiceName(service)} replicas set to ${newReplicas}`
        );

        await this.refreshData();
      } catch (error) {
        console.error('Failed to scale service:', error);
        this.$notify.error(
            'Scaling Failed',
            `Failed to scale ${this.getServiceName(service)}`
        );
      }
    }
  },

  mounted() {
    this.refreshData();
    this.refreshInterval = setInterval(() => {
      if (!this.loading) {
        this.refreshData();
      }
    }, 60000);

    this.metricsInterval = setInterval(() => {
      if (!this.loading) {
        this.fetchMetrics();
      }
    }, 10000);
  },

  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.metricsInterval) {
      clearInterval(this.metricsInterval);
    }
  }
}
</script>

<style scoped>
.metric-card {
  transition: all 0.3s;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.service-card {
  transition: all 0.3s;
}

.stack-header {
  background-color: #f5f5f5;
}

.stack-header:hover {
  background-color: #eeeeee;
}

.caption {
  line-height: 1.2;
}

.scale-control {
  background-color: #f5f5f5;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 1px solid #e0e0e0;
}

.scale-btn {
  min-width: 24px !important;
  width: 24px;
  height: 24px;
  padding: 0 !important;
  margin: 0 !important;
  border-radius: 0 !important;
  background-color: #f5f5f5;
}

.scale-btn:hover {
  background-color: #e0e0e0 !important;
}

.scale-value {
  min-width: 24px;
  text-align: center;
  padding: 0 4px;
  border-left: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  background-color: white;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  text-decoration: underline;
  opacity: 0.8;
}
.global-service {
  background-color: #E3F2FD !important;  /* Light blue */
}

.innilabs-service {
  background-color: #E8F5E9 !important;  /* Light green */
}

.other-service {
  background-color: #F5F5F5 !important;  /* Light grey */
}
</style>